import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { lmsOverview, lmsLiveDemoUrl } from '../../data/lmsPlatform';

const Section = styled.section`
  padding: 72px 0;
  background: linear-gradient(180deg, #f0faf4 0%, #fff 100%);
`;

const Card = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  padding: 40px 44px;
  border-radius: 24px;
  background: linear-gradient(135deg, #0e6b3c 0%, #1e3a5f 100%);
  color: #fff;
  box-shadow: 0 24px 56px rgba(14, 107, 60, 0.25);
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: rgba(109, 187, 26, 0.15);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 32px 28px;
  }
`;

const Label = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #6dbb1a;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  margin: 0 0 14px;
  line-height: 1.2;
`;

const Desc = styled.p`
  font-size: 0.92rem;
  line-height: 1.7;
  opacity: 0.92;
  margin: 0 0 20px;
  max-width: 480px;
`;

const Highlights = styled.ul`
  margin: 0 0 24px;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }

  li {
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 8px;
    opacity: 0.95;

    i {
      color: #6dbb1a;
      font-size: 0.7rem;
    }
  }
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 100px;
  background: #fff;
  color: #0e6b3c;
  font-weight: 700;
  font-size: 0.88rem;
  font-family: var(--font-display);
  text-decoration: none;
`;

const GhostBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-weight: 600;
  font-size: 0.88rem;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MockGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  div {
    padding: 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);

    i {
      font-size: 1.1rem;
      color: #6dbb1a;
      margin-bottom: 8px;
      display: block;
    }

    strong {
      display: block;
      font-family: var(--font-display);
      font-size: 0.85rem;
      margin-bottom: 4px;
    }

    span {
      font-size: 0.68rem;
      opacity: 0.8;
    }
  }
`;

const highlights = [
  'Exams & question bank',
  'Courses & live classes',
  'AI study plans',
  'Shop & payments',
  'Admin panel & CMS',
  'Mobile app + FCM',
];

const LmsProductBanner = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Section ref={ref} id="lms">
      <div className="container">
        <Card
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <div>
            <Label>Our Product</Label>
            <Title>{lmsOverview.title}</Title>
            <Desc>{lmsOverview.tagline}</Desc>
            <Highlights>
              {highlights.map((h) => (
                <li key={h}>
                  <i className="fas fa-check-circle" />
                  {h}
                </li>
              ))}
            </Highlights>
            <BtnRow>
              <PrimaryBtn href={lmsLiveDemoUrl} target="_blank" rel="noopener noreferrer">
                Live demo <i className="fas fa-external-link-alt" />
              </PrimaryBtn>
              <GhostBtn to="/lms">All features</GhostBtn>
              <GhostBtn to="/contact">Request a demo</GhostBtn>
            </BtnRow>
          </div>
          <MockGrid>
            <div>
              <i className="fas fa-chart-pie" />
              <strong>Student dashboard</strong>
              <span>XP, badges, activity</span>
            </div>
            <div>
              <i className="fas fa-file-alt" />
              <strong>Tests & analytics</strong>
              <span>Timed exams, AI plans</span>
            </div>
            <div>
              <i className="fas fa-video" />
              <strong>Live classes</strong>
              <span>Schedule & recordings</span>
            </div>
            <div>
              <i className="fas fa-cogs" />
              <strong>Admin settings</strong>
              <span>Payments, AI, SMS</span>
            </div>
          </MockGrid>
        </Card>
      </div>
    </Section>
  );
};

export default LmsProductBanner;
